import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSystem } from '@ohif/core';
import PageLayout from '../../components/layouts/PageLayout';
import { DataTable, Pagination, TableColumn } from '../../components/common';
import FormSelectVirtualized from '../../components/common/FormSelectVirtualized';
import { Icons } from '@ohif/ui-next';
import EmptyState from '../../components/common/EmptyState';
import ChangePasswordModal from '../../components/common/ChangePasswordModal';
import facilityService from '../../services/facilityService';
import userService from '../../services/userService';
import authService from '../../auth/authService';
import { mapApiResponseToTableFormat } from '../../utils/facilityMapper';
import { formatDateTimeShort } from '../../utils/dateFormatter';
import type {
  Facility,
  FacilityStatus,
  FacilityType,
  PaginationInfo,
} from '../../types/facility.types';
import './FacilityManagement.css';
import './UserManagement.css';
import {
  Eye,
  Edit,
  MoreVertical,
  Plus,
  Search,
  Building2,
  CheckCircle2,
  Users as UsersIcon,
} from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import FacilityCreateEditDialog from './FacilityCreateEditDialog';
import FacilityDetailsDialog from './FacilityDetailsDialog';

// Table row interface for display (DataTable requires Record<string, unknown>)
type FacilityTableRow = Record<string, unknown> & {
  id: string;
  slNo: number;
  active: boolean;
  facilityId: string;
  name: string;
  type?: string;
  emailId: string;
  adminName: string;
  adminPhNo: string;
  createdBy: string;
  createdAt: string;
  lastModifiedBy?: string;
  lastModified?: string;
  changePassword?: React.ReactNode; // For rendering change password button
  actions?: React.ReactNode;
};

const FacilityManagement: React.FC = () => {
  const navigate = useNavigate();
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [statusFilter, setStatusFilter] = useState<FacilityStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<FacilityType | ''>('');
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [selectedFacilityForPasswordChange, setSelectedFacilityForPasswordChange] =
    useState<Facility | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editFacilityId, setEditFacilityId] = useState<number | string | null>(null);
  const [viewFacilityId, setViewFacilityId] = useState<number | string | null>(null);
  const [actionMenuRowId, setActionMenuRowId] = useState<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<{ top: number; right: number } | null>(
    null
  );
  const actionMenuPortalRef = useRef<HTMLDivElement | null>(null);
  const [systemTotalUsers, setSystemTotalUsers] = useState<number | null>(null);

  const loadSystemTotalUserCount = useCallback(async () => {
    try {
      const res = await userService.getAllUsers(1);
      if (res.success) {
        if (res.pagination?.totalUsers != null) {
          setSystemTotalUsers(res.pagination.totalUsers);
        } else {
          setSystemTotalUsers(res.data?.length ?? 0);
        }
      } else {
        setSystemTotalUsers(0);
      }
    } catch {
      setSystemTotalUsers(0);
    }
  }, []);

  useEffect(() => {
    void loadSystemTotalUserCount();
  }, [loadSystemTotalUserCount]);

  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  const loadFacilitiesPage = useCallback(
    async (page: number, search?: string) => {
      setError(null);

      try {
        const response = await facilityService.getAllFacilities(
          page,
          search ? { search } : undefined
        );

        if (response.success && response.data) {
          setFacilities(response.data);
          if (response.pagination) {
            setPagination(response.pagination);
          }

          setIsInitialLoad(false);
        } else {
          const errorMessage = response.error || 'Failed to load facilities';
          setError(errorMessage);
          setIsInitialLoad(false);

          if (uiNotificationService) {
            uiNotificationService.show({
              title: 'Error',
              message: errorMessage,
              type: 'error',
              duration: 5000,
            });
          }
        }
      } catch (error) {
        setIsInitialLoad(false);
        console.error('FacilityManagement - Error fetching facilities:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load facilities';
        setError(errorMessage);

        if (uiNotificationService) {
          uiNotificationService.show({
            title: 'Error',
            message: errorMessage,
            type: 'error',
            duration: 5000,
          });
        }
      }
    },
    [uiNotificationService]
  );

  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const lastFetchKeyRef = useRef('');

  useEffect(() => {
    const searchChanged = prevDebouncedSearchRef.current !== debouncedSearch;
    prevDebouncedSearchRef.current = debouncedSearch;

    let pageToLoad = currentPage;
    if (searchChanged && currentPage !== 1) {
      setCurrentPage(1);
      pageToLoad = 1;
    }

    const fetchKey = `${debouncedSearch}\0${pageToLoad}`;
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;

    void loadFacilitiesPage(pageToLoad, debouncedSearch || undefined);
  }, [currentPage, debouncedSearch, loadFacilitiesPage]);

  // Calculate pagination values from backend or use defaults
  const itemsPerPage = pagination?.facilitiesPerPage || 10;
  const totalItems = pagination?.totalFacilities || 0;

  const handleAddFacility = () => {
    setCreateOpen(true);
  };

  const showFacilityNotify = (
    title: string,
    message: string,
    type: 'success' | 'error',
    duration = 3000
  ) => {
    if (!uiNotificationService) {
      return;
    }
    uiNotificationService.show({ title, message, type, duration });
  };

  const handleToggleActive = async (
    facility: FacilityTableRow,
    index: number,
    checked: boolean
  ) => {
    const originalFacility = facilities.find(
      f => f.id?.toString() === facility.id || f.facility_code === facility.facilityId
    );

    if (!originalFacility || !originalFacility.id) {
      showFacilityNotify('Error', 'Facility ID not found', 'error');
      return;
    }

    const newStatus = checked ? 'Active' : 'Inactive';
    const facilityId = originalFacility.id;

    // Optimistically update the UI
    setFacilities(prev =>
      prev.map(f => {
        if (f.id === facilityId) {
          return { ...f, status: newStatus as 'Active' | 'Inactive' | 'Suspended' };
        }
        return f;
      })
    );

    // Call API to update facility status
    try {
      const response = await facilityService.updateFacility(facilityId, {
        status: newStatus,
      });

      if (response.success) {
        // Show success notification
        showFacilityNotify(
          'Success',
          response.message || `Facility ${newStatus.toLowerCase()} successfully`,
          'success'
        );
      } else {
        // Revert the optimistic update on error
        setFacilities(prev =>
          prev.map(f => {
            if (f.id === facilityId) {
              return { ...f, status: originalFacility.status || 'Active' };
            }
            return f;
          })
        );

        // Show error notification
        showFacilityNotify(
          'Error',
          response.error || 'Failed to update facility status',
          'error',
          5000
        );
      }
    } catch (error) {
      // Revert the optimistic update on error
      setFacilities(prev =>
        prev.map(f => {
          if (f.id === facilityId) {
            return { ...f, status: originalFacility.status || 'Active' };
          }
          return f;
        })
      );

      // Show error notification
      showFacilityNotify(
        'Error',
        error instanceof Error ? error.message : 'Failed to update facility status',
        'error',
        5000
      );
    }
  };

  const handleRowClick = (facility: Facility) => {
    const fid = facility.id != null ? String(facility.id) : '';
    if (!fid) {
      uiNotificationService?.show({
        title: 'Error',
        message: 'Unable to open users: facility ID is missing.',
        type: 'error',
        duration: 4000,
      });
      return;
    }
    navigate('/users', {
      state: {
        facilityId: fid,
        facilityName:
          String(facility.facility_name || facility.facility_code || '').trim() || 'Facility',
      },
    });
  };

  // Search is applied on the backend (`search` query). Status/type still filter the current result set.
  const filteredFacilities = useMemo(() => {
    let filtered = facilities;

    if (statusFilter) {
      filtered = filtered.filter(f => (f.status || 'Active') === statusFilter);
    }

    if (typeFilter) {
      filtered = filtered.filter(f => f.facility_type === typeFilter);
    }

    return filtered;
  }, [facilities, statusFilter, typeFilter]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setTypeFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(statusFilter || typeFilter || searchQuery.trim());

  // Helper function to format phone number with +91 prefix
  const formatPhoneNumber = (phone: string | null | undefined): string => {
    if (!phone) {
      return '--';
    }
    // Remove any existing +91 or spaces
    const cleaned = phone.replace(/^\+91\s*/, '').replace(/\s+/g, '');
    // Add +91 prefix if not already present
    return cleaned ? `+91 ${cleaned}` : '--';
  };

  const handleChangePassword = useCallback(
    (facility: FacilityTableRow) => {
      const originalFacility = facilities.find(
        f => f.id?.toString() === facility.id || f.facility_code === facility.facilityId
      );
      if (originalFacility) {
        setSelectedFacilityForPasswordChange(originalFacility);
        setChangePasswordModalOpen(true);
      }
    },
    [facilities]
  );

  const handlePasswordSubmit = useCallback(
    async (newPassword: string) => {
      if (!selectedFacilityForPasswordChange?.user_id) {
        throw new Error('User ID is required to change password');
      }

      const response = await authService.updatePassword(
        newPassword,
        selectedFacilityForPasswordChange.user_id
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to change password');
      }

      if (uiNotificationService) {
        uiNotificationService.show({
          title: 'Success',
          message: 'Facility admin password changed successfully',
          type: 'success',
          duration: 3000,
        });
      }
    },
    [selectedFacilityForPasswordChange, uiNotificationService]
  );

  const handleCloseActionMenu = useCallback(() => {
    setActionMenuRowId(null);
    setActionMenuAnchor(null);
  }, []);

  useEffect(() => {
    if (!actionMenuRowId) {
      setActionMenuAnchor(null);
      return;
    }
    const rafId = requestAnimationFrame(() => {
      const trigger = document.querySelector(
        `[data-fm-action-trigger-row-id="${actionMenuRowId}"]`
      ) as HTMLElement | null;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        setActionMenuAnchor({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        });
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [actionMenuRowId]);

  useEffect(() => {
    const el = actionMenuPortalRef.current;
    if (!el || !actionMenuAnchor) {
      return;
    }
    el.style.setProperty('--fm-action-menu-top', `${actionMenuAnchor.top}px`);
    el.style.setProperty('--fm-action-menu-right', `${actionMenuAnchor.right}px`);
  }, [actionMenuAnchor]);

  // Map facilities to table format (no client-side pagination since backend handles it)
  const paginatedFacilities = useMemo(() => {
    return filteredFacilities.map((facility, index) => {
      const tableData = mapApiResponseToTableFormat(facility);
      // Get admin phone from users.mobile_number (from API response)
      const adminPhone = facility.users?.mobile_number || null;
      return {
        ...tableData,
        slNo: (currentPage - 1) * itemsPerPage + index + 1,
        active: facility.status === 'Active',
        adminName: facility.users?.full_name || '--',
        adminPhNo: formatPhoneNumber(adminPhone),
        createdBy: facility.CreatedBy?.full_name || '--',
        createdAt: formatDateTimeShort(facility.created_at || facility.createdAt || '') || '--',
        lastModifiedBy: facility.ModifiedBy?.full_name || '--',
        lastModified: formatDateTimeShort(facility.updated_at || facility.updatedAt || '') || '--',
        type: facility.facility_type,
      };
    });
  }, [filteredFacilities, currentPage, itemsPerPage]);

  const columns: TableColumn<FacilityTableRow>[] = useMemo(() => {
    const allColumns: TableColumn<FacilityTableRow>[] = [
      { key: 'slNo', label: 'Sl.No', width: '56px', align: 'left' },
      { key: 'active', label: 'Active', width: '80px', align: 'center' },
      { key: 'facilityId', label: 'Facility Code', width: '110px' },
      { key: 'name', label: 'Name', width: '190px' },
      {
        key: 'type',
        label: 'Type',
        width: '150px',
        render: (value: unknown) => {
          const v = String(value || '').trim();
          if (!v) {
            return '--';
          }
          const displayType =
            v.toUpperCase() === 'HUB' ? 'Hub' : v.toUpperCase() === 'SPOKE' ? 'Spoke' : v;
          const cls =
            v === 'Diagnostic Center'
              ? 'facility-management-type-pill facility-management-type-pill--diagnostic'
              : v === 'Hospital'
                ? 'facility-management-type-pill facility-management-type-pill--hospital'
                : v.toUpperCase() === 'HUB' || v.toUpperCase() === 'SPOKE'
                  ? 'facility-management-type-pill facility-management-type-pill--clinic'
                  : 'facility-management-type-pill facility-management-type-pill--clinic';
          return <span className={cls}>{displayType}</span>;
        },
      },
      { key: 'emailId', label: 'Email ID', width: '220px' },
      { key: 'adminName', label: 'Admin Name', width: '140px' },
      { key: 'adminPhNo', label: 'Admin Ph No.', width: '140px' },
      { key: 'createdBy', label: 'Created By', width: '120px' },
      { key: 'createdAt', label: 'Created At', width: '120px' },
      { key: 'lastModifiedBy', label: 'Last Modified By', width: '150px' },
      { key: 'lastModified', label: 'Last Modified', width: '120px' },
      /* Width fits three 28px controls + gaps inside border-box cell padding (DataTable). */
      { key: 'actions', label: 'Actions', width: '132px', align: 'center' },
    ];

    return allColumns;
  }, []);

  const tableRows: FacilityTableRow[] = useMemo(() => {
    return paginatedFacilities.map((row, index) => {
      const originalFacility = facilities.find(
        f => f.id?.toString() === row.id || f.facility_code === row.facilityId
      );
      const fid = originalFacility?.id ?? row.facilityId;
      const rowId = String(fid ?? row.id ?? index);
      const isMenuOpen = actionMenuRowId === rowId;
      return {
        ...row,
        actions: (
          <div
            className="facility-management-actions"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              className="facility-management-action-btn"
              onClick={e => {
                e.stopPropagation();
                setViewFacilityId(fid);
              }}
              aria-label="View facility"
              title="View"
            >
              <Eye size={18} />
            </button>
            <button
              type="button"
              className="facility-management-action-btn"
              onClick={e => {
                e.stopPropagation();
                setEditFacilityId(fid);
              }}
              aria-label="Edit facility"
              title="Edit"
            >
              <Edit size={18} />
            </button>
            <div className="facility-management-action-cell">
              <button
                type="button"
                className="facility-management-action-trigger"
                data-fm-action-trigger-row-id={rowId}
                onClick={e => {
                  e.stopPropagation();
                  setActionMenuRowId(actionMenuRowId === rowId ? null : rowId);
                }}
                aria-label="More actions"
                aria-expanded={isMenuOpen}
                title="More"
              >
                <MoreVertical size={20} />
              </button>
            </div>
          </div>
        ),
      };
    });
  }, [paginatedFacilities, facilities, actionMenuRowId]);

  const stats = useMemo(() => {
    const totalFacilities = pagination?.totalFacilities ?? facilities.length;
    const activeFacilities = facilities.filter(f => (f.status || 'Active') === 'Active').length;
    return { totalFacilities, activeFacilities };
  }, [pagination?.totalFacilities, facilities]);

  return (
    <PageLayout
      title="Facility Management"
      hideTitleInHeader
      contentClassName="facility-management-page-content"
      breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Facility Management' }]}
    >
      {isInitialLoad && facilities.length === 0 ? (
        <div className="facility-management-loading">Loading facilities...</div>
      ) : facilities.length === 0 && !hasActiveFilters ? (
        <EmptyState
          title="No Facilities added yet"
          description=""
          actionLabel="Add Facilities"
          onAction={handleAddFacility}
          illustrationType="empty-box"
        />
      ) : (
        <div className="facility-management-container facility-management-container--figma">
          <header className="facility-management-subheader user-management-subheader">
            <h1 className="user-management-subheader-title">Facility Management</h1>
            <button
              type="button"
              onClick={handleAddFacility}
              className="user-management-subheader-add-btn"
            >
              <Plus
                strokeWidth={ICON_STROKE_WIDTH}
                size={18}
              />
              Create Facility
            </button>
          </header>

          <div className="user-management-inner">
            <section className="facility-management-stats">
              <div className="facility-management-stat-card facility-management-stat-card--total">
                <div className="facility-management-stat-top">
                  <span>Total Facilities</span>
                  <Building2 size={16} />
                </div>
                <div className="facility-management-stat-value">{stats.totalFacilities}</div>
              </div>
              <div className="facility-management-stat-card facility-management-stat-card--active">
                <div className="facility-management-stat-top">
                  <span>Active Facilities</span>
                  <CheckCircle2 size={16} />
                </div>
                <div className="facility-management-stat-value">{stats.activeFacilities}</div>
              </div>
              <div className="facility-management-stat-card facility-management-stat-card--users">
                <div className="facility-management-stat-top">
                  <span>Total Users</span>
                  <UsersIcon size={16} />
                </div>
                <div className="facility-management-stat-value">
                  {systemTotalUsers === null ? '—' : systemTotalUsers}
                </div>
              </div>
            </section>

            <section className="facility-management-filterbar facility-management-filters-inline">
              <div className="facility-management-filter-search">
                <span
                  className="facility-management-search-icon"
                  aria-hidden
                >
                  <Search size={16} />
                </span>
                <input
                  className="facility-management-search-input"
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="facility-management-filter-wrapper facility-management-filter-wrapper-status">
                <FormSelectVirtualized
                  label=""
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' },
                    { value: 'Suspended', label: 'Suspended' },
                  ]}
                  value={statusFilter}
                  onChange={e => setStatusFilter((e.target.value || '') as FacilityStatus | '')}
                  placeholder="All Status"
                  name="statusFilter"
                />
              </div>
              <div className="facility-management-filter-wrapper facility-management-filter-wrapper-type">
                <FormSelectVirtualized
                  label=""
                  options={[
                    { value: '', label: 'All types' },
                    { value: 'Diagnostic Center', label: 'Diagnostic Center' },
                    { value: 'Hospital', label: 'Hospital' },
                    { value: 'Clinic', label: 'Clinic' },
                    { value: 'HUB', label: 'Hub' },
                    { value: 'SPOKE', label: 'Spoke' },
                  ]}
                  value={typeFilter}
                  onChange={e => setTypeFilter((e.target.value || '') as FacilityType | '')}
                  placeholder="All types"
                  name="typeFilter"
                />
              </div>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="facility-management-clear-all"
                >
                  <Icons.Close className="h-5 w-5" />
                  Clear All
                </button>
              ) : null}
            </section>

            {/* Table + pagination in one column (same idea as imaging worklist): footer sits under last row; only the table area scrolls when tall. */}
            <div className="facility-management-table-scroll">
              <div className="facility-management-table-container">
                <DataTable
                  columns={columns}
                  data={tableRows}
                  onRowClick={row => {
                    // Find the original facility from the facilities array
                    const originalFacility = facilities.find(
                      f => f.id?.toString() === row.id || f.facility_code === row.facilityId
                    );
                    if (originalFacility) {
                      handleRowClick(originalFacility);
                    }
                  }}
                  onToggleActive={handleToggleActive}
                  getRowKey={row => row.id}
                  fixedRightColumnKey="actions"
                  pageScrollStickyHeader
                />
              </div>
              <div className="facility-management-pagination">
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <FacilityCreateEditDialog
        open={createOpen}
        mode="create"
        onOpenChange={open => setCreateOpen(open)}
        onSuccess={() => {
          setCreateOpen(false);
          lastFetchKeyRef.current = '';
          if (currentPage === 1) {
            void loadFacilitiesPage(1, debouncedSearch || undefined);
          } else {
            setCurrentPage(1);
          }
          void loadSystemTotalUserCount();
          uiNotificationService?.show({
            title: 'Success',
            message: 'Facility created successfully',
            type: 'success',
            duration: 3000,
          });
        }}
      />

      <FacilityCreateEditDialog
        open={editFacilityId != null}
        mode="edit"
        facilityId={editFacilityId ?? undefined}
        onOpenChange={open => !open && setEditFacilityId(null)}
        onSuccess={() => {
          setEditFacilityId(null);
          lastFetchKeyRef.current = '';
          void loadFacilitiesPage(currentPage, debouncedSearch || undefined);
          void loadSystemTotalUserCount();
          uiNotificationService?.show({
            title: 'Success',
            message: 'Facility updated successfully',
            type: 'success',
            duration: 3000,
          });
        }}
      />

      <FacilityDetailsDialog
        open={viewFacilityId != null}
        facilityId={viewFacilityId ?? undefined}
        onOpenChange={open => !open && setViewFacilityId(null)}
      />

      {actionMenuRowId != null &&
        actionMenuAnchor != null &&
        createPortal(
          <>
            <div
              className="facility-management-action-backdrop facility-management-action-backdrop-portal"
              onClick={handleCloseActionMenu}
              aria-hidden
            />
            <div
              ref={actionMenuPortalRef}
              className="facility-management-action-menu facility-management-action-menu-portal"
            >
              <button
                type="button"
                className="facility-management-action-menu-item"
                onClick={e => {
                  e.stopPropagation();
                  const openFacility = facilities.find(f => String(f.id) === actionMenuRowId);
                  if (openFacility) {
                    const openIndex = filteredFacilities.findIndex(
                      f => String(f.id) === actionMenuRowId
                    );
                    const tableData = mapApiResponseToTableFormat(openFacility);
                    const adminPhone = openFacility.users?.mobile_number || null;
                    const rowForHandler: FacilityTableRow = {
                      ...tableData,
                      slNo: (currentPage - 1) * itemsPerPage + Math.max(openIndex, 0) + 1,
                      active: openFacility.status === 'Active',
                      adminName: openFacility.users?.full_name || '--',
                      adminPhNo: formatPhoneNumber(adminPhone),
                      createdBy: openFacility.CreatedBy?.full_name || '--',
                      createdAt:
                        formatDateTimeShort(
                          openFacility.created_at || openFacility.createdAt || ''
                        ) || '--',
                      lastModifiedBy: openFacility.ModifiedBy?.full_name || '--',
                      lastModified:
                        formatDateTimeShort(
                          openFacility.updated_at || openFacility.updatedAt || ''
                        ) || '--',
                      type: openFacility.facility_type,
                    };
                    handleCloseActionMenu();
                    handleChangePassword(rowForHandler);
                  }
                }}
              >
                Change Password
              </button>
            </div>
          </>,
          document.body
        )}

      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        onClose={() => {
          setChangePasswordModalOpen(false);
          setSelectedFacilityForPasswordChange(null);
        }}
        onSubmit={handlePasswordSubmit}
        title="Change Password"
      />
    </PageLayout>
  );
};

export default FacilityManagement;
